import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type { SessionView, SimulationSpeed } from '../game/model/session'
import { getSession } from '../game/model/sessionHost'
import { getAudioManager } from '../game/audio'
import type { MapDefinition } from '../game/model/types'
import { DEFAULT_MAP_ID } from '../game/model/mapCatalog'
import type { DifficultyId } from '../game/combat/types/primitives'
import type { GameState, Difficulty, GameConfig } from '../types'
import { getStage } from '../game/campaign'

interface GameStore {
  gameState: GameState
  selectedMapId: string
  /** Campaign stage when playing Chapter content (null = legacy/debug map play). */
  selectedStageId: string | null
  selectedDifficultyId: DifficultyId
  difficulty: Difficulty
  isPaused: boolean
  gameSpeed: number

  /** Mirror of GameSession for the HUD. Not a second simulation. */
  sessionView: SessionView | null

  /** UI-only placement selection. Not spent until GameSession.placeTower succeeds. */
  placementDefinitionId: string | null

  /** UI-only selected placed tower. Upgrade/sell go through GameSession. */
  selectedTowerId: string | null

  /**
   * Optional in-memory map for editor playtest.
   * When set, PlayingScene uses this clone instead of the catalog.
   */
  playtestMap: MapDefinition | null

  startGame: (config: GameConfig) => void
  /** Start a campaign stage match. */
  startStage: (stageId: string, difficultyId?: DifficultyId) => void
  startPlaytest: (map: MapDefinition) => void
  endPlaytest: () => void
  pauseGame: () => void
  resumeGame: () => void
  setGameSpeed: (speed: number) => void
  publishSessionView: (view: SessionView) => void
  setPlacementDefinition: (definitionId: string | null) => void
  cancelPlacement: () => void
  selectTowerId: (towerId: string | null) => void
  upgradeTower: (towerId: string) => boolean
  sellTower: (towerId: string) => boolean
  startWave: () => boolean
  resetGame: () => void
}

export const useGameStore = create<GameStore>()(
  devtools(
    (set, get) => ({
      gameState: 'menu',
      selectedMapId: DEFAULT_MAP_ID,
      selectedStageId: null,
      selectedDifficultyId: 'normal',
      difficulty: 'medium',
      isPaused: false,
      gameSpeed: 1,

      sessionView: null,
      placementDefinitionId: null,
      selectedTowerId: null,
      playtestMap: null,

      startGame: (config: GameConfig) => {
        set({
          gameState: 'playing',
          selectedMapId: config.mapId,
          selectedStageId: null,
          difficulty: config.difficulty,
          isPaused: false,
          gameSpeed: 1,
          sessionView: null,
          placementDefinitionId: null,
          selectedTowerId: null,
          playtestMap: null
        })
      },

      startStage: (stageId: string, difficultyId: DifficultyId = 'normal') => {
        const stage = getStage(stageId)
        if (!stage) return
        set({
          gameState: 'playing',
          selectedMapId: stage.mapId,
          selectedStageId: stageId,
          selectedDifficultyId: difficultyId,
          difficulty: 'medium',
          isPaused: false,
          gameSpeed: 1,
          sessionView: null,
          placementDefinitionId: null,
          selectedTowerId: null,
          playtestMap: null
        })
      },

      startPlaytest: (map: MapDefinition) => {
        set({
          gameState: 'playing',
          selectedMapId: map.id,
          selectedStageId: null,
          difficulty: 'medium',
          isPaused: false,
          gameSpeed: 1,
          sessionView: null,
          placementDefinitionId: null,
          selectedTowerId: null,
          playtestMap: structuredClone(map)
        })
      },

      endPlaytest: () => {
        set({
          gameState: 'menu',
          selectedMapId: DEFAULT_MAP_ID,
          selectedStageId: null,
          difficulty: 'medium',
          isPaused: false,
          gameSpeed: 1,
          sessionView: null,
          placementDefinitionId: null,
          selectedTowerId: null,
          playtestMap: null
        })
      },

      pauseGame: () => {
        getSession()?.setPaused(true)
        const view = getSession()?.view()
        set(view
          ? { isPaused: true, sessionView: view, placementDefinitionId: null }
          : { isPaused: true, placementDefinitionId: null })
      },

      resumeGame: () => {
        getSession()?.setPaused(false)
        const view = getSession()?.view()
        set(view ? { isPaused: false, sessionView: view } : { isPaused: false })
      },

      setGameSpeed: (speed: number) => {
        const simulationSpeed: SimulationSpeed = speed === 2 || speed === 3 ? speed : 1
        getSession()?.setSimulationSpeed(simulationSpeed)
        const view = getSession()?.view()
        set(view
          ? { gameSpeed: simulationSpeed, sessionView: view }
          : { gameSpeed: simulationSpeed })
      },

      publishSessionView: (view: SessionView) => {
        const selectedTowerId = get().selectedTowerId
        const stillExists =
          selectedTowerId && getSession()?.getTower(selectedTowerId)
            ? selectedTowerId
            : null
        set({
          sessionView: view,
          isPaused: view.paused,
          gameSpeed: view.simulationSpeed,
          selectedTowerId: stillExists,
          ...(view.paused ? { placementDefinitionId: null } : {})
        })
      },

      setPlacementDefinition: (definitionId: string | null) => {
        if (get().isPaused) return
        set({
          placementDefinitionId: definitionId,
          ...(definitionId ? { selectedTowerId: null } : {})
        })
      },

      cancelPlacement: () => {
        set({ placementDefinitionId: null })
      },

      selectTowerId: (towerId: string | null) => {
        set({
          selectedTowerId: towerId,
          ...(towerId ? { placementDefinitionId: null } : {})
        })
      },

      upgradeTower: (towerId: string) => {
        const session = getSession()
        if (!session) return false
        const result = session.upgradeTower(towerId)
        set({ sessionView: session.view() })
        return result.ok
      },

      sellTower: (towerId: string) => {
        const session = getSession()
        if (!session) return false
        const result = session.sellTower(towerId)
        if (!result.ok) return false
        const selectedTowerId = get().selectedTowerId
        set({
          sessionView: session.view(),
          selectedTowerId: selectedTowerId === towerId ? null : selectedTowerId
        })
        return true
      },

      startWave: () => {
        const session = getSession()
        if (!session) return false
        const result = session.startWave({ early: true })
        if (result.ok) {
          getAudioManager().unlock()
          getAudioManager().play('ui.waveStart')
        }
        set({ sessionView: session.view() })
        return result.ok
      },

      resetGame: () => {
        set({
          gameState: 'menu',
          selectedMapId: DEFAULT_MAP_ID,
          selectedStageId: null,
          difficulty: 'medium',
          isPaused: false,
          gameSpeed: 1,
          sessionView: null,
          placementDefinitionId: null,
          selectedTowerId: null,
          playtestMap: null
        })
      }
    }),
    {
      name: 'shapes-td-game-store'
    }
  )
)
